/**
 * Which part of the product each screen belongs to.
 *
 * A company is sold modules - the asset register, the service desk, maintenance - and the server
 * refuses the API of one it was not sold. This maps the app's own routes to those modules so the
 * menu offers only what the company actually has, rather than leading people to a screen that will
 * answer "not part of your subscription".
 *
 * A route no module claims is always shown. Signing in, the dashboard, settings and the audit trail
 * belong to everybody, and a screen added without a line here keeps working rather than quietly
 * disappearing.
 */
const ROUTE_MODULES: ReadonlyArray<readonly [string, string]> = [
  ['/service-desk', 'SERVICE_DESK'],
  ['/maintenance', 'MAINTENANCE'],
  ['/work-orders', 'MAINTENANCE'],
  ['/contracts', 'CONTRACTS'],
  ['/consumables', 'INVENTORY'],
  ['/stocktakes', 'INVENTORY'],
  ['/procurement', 'PROCUREMENT'],
  ['/preparation', 'PROCUREMENT'],
  ['/endpoints', 'ENDPOINTS'],
  ['/device-groups', 'ENDPOINTS'],
  ['/policies', 'ENDPOINTS'],
  ['/policy-deployments', 'ENDPOINTS'],
  ['/policy-events', 'ENDPOINTS'],
  ['/software', 'ENDPOINTS'],
  ['/discovery', 'DISCOVERY'],
  ['/software-licences', 'SOFTWARE_LICENCES'],
  ['/decommission', 'DECOMMISSION'],
  ['/data-sanitisation', 'DECOMMISSION'],
  ['/reports', 'REPORTS'],
];

/** The module a route belongs to, or null when it belongs to no particular one. */
export function moduleForRoute(path: string): string | null {
  const match = ROUTE_MODULES.find(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`),
  );
  return match ? match[1] : null;
}

/**
 * Whether a route should be offered, given what the company has.
 *
 * Only ever hides; it never grants. The server is what refuses an unlicensed module, and it does so
 * whether or not this agreed.
 */
export function routeInLicensedModule(path: string, hasModule: (module: string) => boolean): boolean {
  const module = moduleForRoute(path);
  return module === null || hasModule(module);
}
